/**
 * BeezFy Performance Optimizer
 * Sistema globale per ottimizzare le performance del sito
 * Lazy loading, debouncing, caching e ottimizzazioni varie
 */

(function() {
  'use strict';

  // Polyfill per requestIdleCallback
  if (!window.requestIdleCallback) {
    window.requestIdleCallback = function(callback, options) {
      const timeout = options?.timeout || 1000;
      const start = Date.now();
      return setTimeout(function() {
        callback({
          didTimeout: false,
          timeRemaining: function() {
            return Math.max(0, 50.0 - (Date.now() - start));
          }
        });
      }, 1);
    };
  }

  // Polyfill per cancelIdleCallback
  if (!window.cancelIdleCallback) {
    window.cancelIdleCallback = function(id) {
      clearTimeout(id);
    };
  }

  // Lazy Load per immagini
  const lazyLoadImages = () => {
    const images = document.querySelectorAll('img[loading="lazy"]');
    if ('loading' in HTMLImageElement.prototype) {
      // Browser supporta lazy loading nativo
      return;
    }

    // Fallback con Intersection Observer
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src || img.src;
          img.classList.remove('lazy');
          observer.unobserve(img);
        }
      });
    });

    images.forEach(img => imageObserver.observe(img));
  };

  // Debounce utility
  window.debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Throttle utility
  window.throttle = (func, limit) => {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };

  // Cache per fetch requests
  const fetchCache = new Map();
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minuti

  window.cachedFetch = async (url, options = {}) => {
    const cacheKey = `${url}_${JSON.stringify(options)}`;
    const cached = fetchCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return Promise.resolve(cached.data.clone());
    }

    const response = await fetch(url, options);
    const clonedResponse = response.clone();

    fetchCache.set(cacheKey, {
      data: clonedResponse,
      timestamp: Date.now()
    });

    // Pulizia cache vecchia
    if (fetchCache.size > 50) {
      const oldestKey = fetchCache.keys().next().value;
      fetchCache.delete(oldestKey);
    }

    return response;
  };

  // Ottimizzazione scroll
  let scrollTimer;
  const optimizedScroll = throttle(() => {
    document.body.classList.add('is-scrolling');

    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
      document.body.classList.remove('is-scrolling');
    }, 150);
  }, 100);

  // Disabilita animazioni durante lo scroll su mobile
  if (window.matchMedia('(max-width: 768px)').matches) {
    window.addEventListener('scroll', optimizedScroll, { passive: true });
  }

  // Lazy load componenti pesanti
  const lazyLoadComponents = () => {
    // Cart drawer
    const cartIcon = document.querySelector('[data-cart-icon]');
    if (cartIcon) {
      cartIcon.addEventListener('mouseenter', () => {
        import('./beezfy-cart-drawer.js').catch(() => {});
      }, { once: true, passive: true });
    }

    // Variant wizard
    const productCards = document.querySelectorAll('[data-product-card]');
    productCards.forEach(card => {
      card.addEventListener('mouseenter', () => {
        import('./beezfy-variant-wizard-unified.js').catch(() => {});
      }, { once: true, passive: true });
    });

    // Popup
    const addToCartButtons = document.querySelectorAll('[data-add-to-cart]');
    if (addToCartButtons.length > 0) {
      requestIdleCallback(() => {
        import('./beezfy-add-to-cart-popup.js').catch(() => {});
      }, { timeout: 3000 });
    }
  };

  // Ottimizzazione form
  const optimizeForms = () => {
    const forms = document.querySelectorAll('form');
    forms.forEach(form => {
      // Disabilita validazione HTML5 su mobile per performance
      if (window.matchMedia('(max-width: 768px)').matches) {
        form.setAttribute('novalidate', '');
      }

      // Previeni submit multipli
      form.addEventListener('submit', function(e) {
        if (this.dataset.submitting === 'true') {
          e.preventDefault();
          return false;
        }
        this.dataset.submitting = 'true';
        setTimeout(() => {
          this.dataset.submitting = 'false';
        }, 3000);
      });
    });
  };

  // Riduci console.log in produzione
  if (!window.location.hostname.includes('localhost')) {
    const noop = () => {};
    console.log = noop;
    console.debug = noop;
    console.info = noop;
    console.warn = noop;
  }

  // Inizializzazione ottimizzata
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      requestIdleCallback(() => {
        lazyLoadImages();
        lazyLoadComponents();
        optimizeForms();
      }, { timeout: 2000 });
    });
  } else {
    requestIdleCallback(() => {
      lazyLoadImages();
      lazyLoadComponents();
      optimizeForms();
    }, { timeout: 2000 });
  }

  // Cleanup su page hide (mobile)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Cancella timer e animazioni
      if (scrollTimer) clearTimeout(scrollTimer);

      // Pausa video/audio
      document.querySelectorAll('video, audio').forEach(media => {
        if (!media.paused) media.pause();
      });
    }
  });

  // (Service Worker rimosso: Shopify non serve /sw.js dalla radice, ogni visita faceva una richiesta 404)

})();